import React, { Component } from 'react';
import { AuthRoute as Route } from 'react-router-auth';
import { Route as AnonymousRoute, withRouter } from 'react-router';
import styled from 'styled-components';
import asyncComponent from '../../components/AsyncComponent';
import Sidebar from '../../components/Sidebar';

const AsyncDashboard = asyncComponent(() => import('../Dashboard'));
const AsyncCallback = asyncComponent(() => import('../Callback'));
const AsyncSignIn = asyncComponent(() => import('../SignIn'));
const AsyncShops = asyncComponent(() => import('../Shops'));
const AsyncCreateShop = asyncComponent(() => import('../CreateShop'));
const AsyncCreateMenuCategory = asyncComponent(() => import('../CreateMenuCategory'));
const AsyncCreateMenuItem = asyncComponent(() => import('../CreateMenuItem'));
const AsyncEditMenuCategory = asyncComponent(() => import('../EditMenuCategory'));
const AsyncEditMenuItem = asyncComponent(() => import('../EditMenuItem'));
const AsyncEditShop = asyncComponent(() => import('../EditShop'));
const AsyncShopDetails = asyncComponent(() => import('../ShopDetails'));
const AsyncShopMenu = asyncComponent(() => import('../ShopMenu'));
const AsyncEditTradingHours = asyncComponent(() => import('../EditTradingHours'));
const AsyncDeliveryPartners = asyncComponent(() => import('../DeliveryPartners'));
const AsyncCreateDeliveryPartner = asyncComponent(() => import('../CreateDeliveryPartner'));
const AsyncEditDeliveryPartner = asyncComponent(() => import('../EditDeliveryPartner'));
const AsyncCustomers = asyncComponent(() => import('../Customers'));
const AsyncManagers = asyncComponent(() => import('../Managers'));
const AsyncLeads = asyncComponent(() => import('../Leads'));
const AsyncOrders = asyncComponent(() => import('../Orders'));
const AsyncOrderDetails = asyncComponent(() => import('../OrderDetails'));
const AsyncMenuItemExtras = asyncComponent(() => import('../MenuItemExtras'));
const AsyncCreateManager = asyncComponent(() => import('../CreateManager'));
const AsyncEditManager = asyncComponent(() => import('../EditManager'));
const AsyncCreateShopAccount = asyncComponent(() => import('../CreateShopAccount'));
const AsyncEditShopAccount = asyncComponent(() => import('../EditShopAccount'));

const lazyRoutes = [
  AsyncDashboard, AsyncCallback, AsyncSignIn, AsyncShops, AsyncCreateShop,
  AsyncCreateMenuCategory, AsyncCreateMenuItem, AsyncEditMenuCategory,
  AsyncEditMenuItem, AsyncEditShop, AsyncShopDetails, AsyncShopMenu,
  AsyncEditTradingHours, AsyncDeliveryPartners, AsyncCreateDeliveryPartner,
  AsyncEditDeliveryPartner, AsyncCustomers, AsyncManagers, AsyncLeads,
  AsyncOrders, AsyncOrderDetails, AsyncMenuItemExtras, AsyncCreateManager,
  AsyncEditManager, AsyncCreateShopAccount, AsyncEditShopAccount,
];

const shouldPreloadRoutes = () => {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return true;
  if (conn.saveData) return false;
  return !/(^|-)2g$/.test(conn.effectiveType || '');
};

const Wrapper = styled.div`
  -webkit-transition: all 0.5s ease;
  -moz-transition: all 0.5s ease;
  -o-transition: all 0.5s ease;
  transition: all 0.5s ease;
`;

const ContentWrapper = styled.div`
  width: 100%;
  position: absolute;
  padding: 15px 15px 15px 300px;
`;

class App extends Component {
  componentDidMount() {
    if (!shouldPreloadRoutes()) return;
    const preload = () => lazyRoutes.forEach(c => c.preload && c.preload().catch(() => {}));
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(preload, { timeout: 5000 });
    } else {
      setTimeout(preload, 2000);
    }
  }

  logout = () => {
    const { history } = this.props;
    localStorage.removeItem('cloudchefs-access-token');
    localStorage.removeItem('cloudchefs-id-token');
    localStorage.removeItem('cloudchefs-admin-expires-at');
    history.push('/login');
  }

  isAuthenticated = () => {
    const expiresAt = localStorage.getItem('cloudchefs-admin-expires-at');
    if (!expiresAt) return false;
    return new Date().getTime() < expiresAt;
  }

  render() {
    const blacklisted = ['/login', '/auth'];
    return (
      <div>
        <AnonymousRoute exact path='/login' component={AsyncSignIn} />
        <AnonymousRoute exact path='/auth' component={AsyncCallback} />
        <Wrapper>
          {!blacklisted.find(path => path === this.props.location.pathname)
            && <Sidebar logout={this.logout} />}
          <ContentWrapper>
            <Route
              exact
              path='/'
              component={AsyncDashboard}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops'
              component={AsyncShops}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/create'
              component={AsyncCreateShop}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/edit/:id'
              component={AsyncEditShop}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/menu/:id'
              component={AsyncShopMenu}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/details/:id'
              component={AsyncShopDetails}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/:id/category'
              component={AsyncCreateMenuCategory}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/:shopId/category/:id'
              component={AsyncCreateMenuItem}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/:shopId/edit-category/:categoryId'
              component={AsyncEditMenuCategory}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/:id/add-banking-details'
              component={AsyncCreateShopAccount}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/:id/edit-banking-details'
              component={AsyncEditShopAccount}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/categories/:shopId/edit-menu-item/:menuItemId'
              component={AsyncEditMenuItem}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/shops/trading-hours/:id'
              component={AsyncEditTradingHours}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/delivery-partners'
              component={AsyncDeliveryPartners}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/delivery-partners/create'
              component={AsyncCreateDeliveryPartner}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/delivery-partners/edit/:id'
              component={AsyncEditDeliveryPartner}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/customers'
              component={AsyncCustomers}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/managers'
              component={AsyncManagers}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/managers/create'
              component={AsyncCreateManager}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/managers/edit/:id'
              component={AsyncEditManager}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/menu-item-extras'
              component={AsyncMenuItemExtras}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/leads'
              component={AsyncLeads}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/orders'
              component={AsyncOrders}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
            <Route
              exact
              path='/orders/:uuid'
              component={AsyncOrderDetails}
              redirectTo='/login'
              authenticated={this.isAuthenticated()}
            />
          </ContentWrapper>
        </Wrapper>
      </div>
    );
  }
}

export default withRouter(App);
